import { loginSchema } from '@/components/auth/login';
import { router } from '@/main';
import axios, { AxiosError } from 'axios';
import { toast } from 'sonner';
import { z } from 'zod';
import { OrderItem, RegisterFormData } from '../types/index';
import { handleApiError } from './utils';

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Keep the current access token in memory; initialise from localStorage once.
let accessToken: string | null = localStorage.getItem('liga:token');

// Share a single refresh request between parallel 401 responses.
let refreshPromise: Promise<string> | null = null;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true, // Important for sending/receiving cookies
});

const setToken = (token: string) => {
  if (!token) {
    console.warn('Attempted to set empty token');
    return;
  }

  accessToken = token;
  localStorage.setItem('liga:token', token);

  const tokenWithBearer = token.startsWith('Bearer ')
    ? token
    : `Bearer ${token}`;
  api.defaults.headers.common['Authorization'] = tokenWithBearer;
  console.debug('Token set:', tokenWithBearer);
};

export const clearTokens = () => {
  accessToken = null;
  localStorage.removeItem('liga:token');
  localStorage.removeItem('liga:userData');
  delete api.defaults.headers.common['Authorization'];
};

const initializeToken = () => {
  if (accessToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  }
};

// Setup interceptors
api.interceptors.request.use(
  (config) => {
    if (accessToken) {
      const tokenWithBearer = accessToken.startsWith('Bearer ')
        ? accessToken
        : `Bearer ${accessToken}`;
      config.headers['Authorization'] = tokenWithBearer;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as typeof error.config & {
      _retry?: boolean;
    };

    // Endpoints that should never attempt refresh
    const skipRefreshEndpoints = [
      '/api/auth/login',
      '/api/auth/refresh',
      '/api/auth/register',
      '/api/auth/verify',
    ];

    if (
      skipRefreshEndpoints.some((endpoint) =>
        originalRequest.url?.includes(endpoint)
      )
    ) {
      return Promise.reject(error);
    }

    // Only react to 401 responses
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Prevent infinite retry loops
    if (originalRequest._retry) {
      clearTokens();
      router.navigate({ to: '/login' });
      toast.error('A munkamenet lejárt. Kérjük, jelentkezzen be újra.');
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      // Share a single refresh request among all failed requests
      if (!refreshPromise) {
        refreshPromise = api
          .get('/api/auth/refresh')
          .then((res) => res.data.token)
          .finally(() => {
            refreshPromise = null;
          });
      }

      const newToken = await refreshPromise;
      setToken(newToken);

      // Retry the original request with the new token
      originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      clearTokens();
      router.navigate({ to: '/login' });
      return Promise.reject(refreshError);
    }
  }
);

initializeToken();

export const isAuthenticated = () => {
  return !!localStorage.getItem('liga:token');
};

export const register = async (formData: RegisterFormData) => {
  try {
    const { terms, confirmPassword, ...dataToSend } = formData;
    const response = await api.post('/api/auth/register', dataToSend);

    toast.success('Regisztráció sikeres!');
    return response.data;
  } catch (error) {
    const errorMessage = handleApiError(error, {
      defaultMessage: 'Registration failed',
      toastMessage: 'Regisztráció sikertelen!',
    });
    throw new Error(errorMessage);
  }
};

export const login = async (values: z.infer<typeof loginSchema>) => {
  try {
    const response = await api.post('/api/auth/login', values);
    const { token } = response.data;

    if (!token) {
      throw new Error('No token received from server');
    }

    setToken(token);
    console.debug('Login successful, fetching user data...');

    // Force refresh user data after login
    const me = await getMe({ forceRefresh: true });
    if (!me) {
      throw new Error('Failed to fetch user data after login');
    }

    // Update localStorage with fresh data and timestamp
    localStorage.setItem('liga:userData', JSON.stringify(me));
    localStorage.setItem('liga:userDataTimestamp', Date.now().toString());

    if (me.state === 'loggedIn') {
      router.navigate({ to: '/' });
    } else {
      router.navigate({ to: '/waiting' });
    }

    return response.data;
  } catch (error) {
    clearTokens(); // Clear any potentially invalid tokens
    const errorMessage = handleApiError(error, {
      defaultMessage: 'Login failed',
      toastMessage: 'Bejelentkezés sikertelen!',
    });
    throw new Error(errorMessage);
  }
};

export const verifyEmail = async (token: string) => {
  try {
    const response = await api.get(`/api/auth/verify?token=${token}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      // If we get a 400 or 401, it means the token is invalid/expired - don't try to refresh
      if (error.response?.status === 400 || error.response?.status === 401) {
        throw new Error(
          'A megerősítő link érvénytelen vagy lejárt. Kérjük, kérjen új megerősítő emailt.'
        );
      }

      // For other error responses with a message, use that
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
    }

    // Default error message
    throw new Error(
      'Az email cím megerősítése sikertelen volt. Kérjük, próbálja újra.'
    );
  }
};

export const getMe = async (options?: { forceRefresh?: boolean }) => {
  const forceRefresh = options?.forceRefresh || false;

  // Check localStorage first if not forcing refresh
  if (!forceRefresh) {
    const cachedUserData = localStorage.getItem('liga:userData');
    const timestamp = localStorage.getItem('liga:userDataTimestamp');

    if (cachedUserData && timestamp) {
      try {
        const userData = JSON.parse(cachedUserData);
        const dataAge = Date.now() - parseInt(timestamp, 10);

        // Only use cached data if it's less than 1 minute old
        if (dataAge < 1 * 60 * 1000) {
          console.debug('Using cached user data');
          return userData;
        } else {
          console.debug('Cached user data is too old, fetching fresh data');
        }
      } catch (e) {
        console.error('Error parsing cached user data', e);
      }
    }
  } else {
    console.debug('Force refreshing user data');
  }

  try {
    const response = await api.get('/api/users/me');
    const userData = response.data;

    // Cache the fresh data
    localStorage.setItem('liga:userData', JSON.stringify(userData));
    localStorage.setItem('liga:userDataTimestamp', Date.now().toString());
    console.debug('Fetched and cached fresh user data', userData);

    return userData;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to fetch user data',
    });
    return null;
  }
};

export const generateTOTP = async ({
  isEmail = false,
}: {
  isEmail?: boolean;
}) => {
  try {
    const response = await api.post('/api/auth/otp/generate', { isEmail });
    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to generate TOTP',
    });
    return null;
  }
};

export const verifyTOTP = async (digits: string) => {
  try {
    const response = await api.post('/api/auth/otp/verify', { digits });

    // After successful verification, update user data
    await getMe({ forceRefresh: true });

    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to verify TOTP',
    });
    return null;
  }
};

export const resendOTPEmail = async () => {
  try {
    const response = await api.get('/api/auth/otp/resend-email');
    toast.success('Email sikeresen elküldve!');
    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to resend email',
      toastMessage: 'Email küldése sikertelen!',
    });
    throw error;
  }
};

export const getProducts = async () => {
  try {
    const response = await api.get('/api/products');
    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to fetch products',
    });
    return null;
  }
};

export const orderProducts = async (items: OrderItem[]) => {
  try {
    const response = await api.post('/api/orders/add-items', items);
    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to order product',
    });
    return null;
  }
};

export const sendPasswordResetEmail = async (email: string) => {
  try {
    const response = await api.post('/api/auth/send-reset-password-email', {
      email,
    });
    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to send password reset email',
      toastMessage: 'Sikertelen jelszó visszaállítási kérelem!',
    });
    return null;
  }
};

export const resetPassword = async (password: string, token: string) => {
  try {
    const response = await api.post(`/api/auth/reset-password?token=${token}`, {
      password,
    });
    toast.success('Jelszó sikeresen megváltoztatva!');
    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to reset password',
      toastMessage: 'Sikertelen jelszó módosítás!',
    });
    return null;
  }
};

export const updatePartnerData = async (
  partnerData: Partial<{
    firstName: string;
    lastName: string;
    phone: string;
    partnerName: string;
    partnerAddress: string;
    partnerDeliveryName?: string;
    partnerDeliveryAddress?: string;
  }>
) => {
  try {
    const response = await api.put('/api/users/partner-data', partnerData);
    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to update partner data',
      toastMessage: 'Partner adatok frissítése sikertelen!',
    });
    return null;
  }
};

export const resendVerification = async (email: string) => {
  try {
    const response = await api.post('/api/auth/resend-verification', { email });
    toast.success('Megerősítő email elküldve!');
    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to resend verification email',
      toastMessage: 'Megerősítő email küldése sikertelen!',
    });
    return null;
  }
};

export const getCreditPool = async () => {
  try {
    const response = await api.get('/api/orders/credit-pool');

    // The backend returns an object with poolSize and totalUsedCredits
    return response.data as {
      poolSize: number;
      totalUsedCredits: number;
    };
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to fetch credit pool',
    });
    return null;
  }
};

export const getRules = async () => {
  try {
    const response = await api.get('/api/pages/rules');
    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to fetch rules',
      toastMessage: 'Szabályzat betöltése sikertelen!',
    });
    return null;
  }
};
export const getPrivacy = async () => {
  try {
    const response = await api.get('/api/pages/privacy');
    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to fetch privacy',
      toastMessage: 'Adatvédelmi Tájékoztató betöltése sikertelen!',
    });
    return null;
  }
};

export const getMissions = async () => {
  try {
    const response = await api.get('/api/missions');
    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to fetch missions',
    });
    return null;
  }
};

export const getMission = async (id: number) => {
  try {
    const response = await api.get(`/api/missions/${id}`);
    return response.data;
  } catch (error) {
    handleApiError(error, {
      defaultMessage: 'Failed to fetch mission',
    });
    return null;
  }
};
